import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import Text from '../../components/Text';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import styles from './Demolish.module.css';
import { firstError } from '../../api/envelope';

const DEFAULT_ANVIL_RADIUS = 8;
const DEFAULT_REPAIR_BELOW_PERCENT = 75;
const DEFAULT_MAX_LEVELS = 39;

interface RepairConfig {
    id: string;
    name: string;
    storageId: string;
    anvilRadius: number;
    repairBelowPercent: number;
    maxLevelsPerRepair: number;
    isEnabled: boolean;
    createdAt: string;
}

export default function RepairPage() {
    return <BotScoped groupAware><RepairInner /></BotScoped>;
}

function RepairInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [configs, setConfigs] = createSignal<RepairConfig[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formAnvilRadius, setFormAnvilRadius] = createSignal(DEFAULT_ANVIL_RADIUS);
    const [formRepairBelowPercent, setFormRepairBelowPercent] = createSignal(DEFAULT_REPAIR_BELOW_PERCENT);
    const [formMaxLevels, setFormMaxLevels] = createSignal(DEFAULT_MAX_LEVELS);
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';
    const inv = useInventoryApi();

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.repair.getRepairConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs((cfgRes.data.value.configs ?? []) as RepairConfig[]);
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('REPAIR_SAVE_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.repair.getRepairConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as RepairConfig[]);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormStorageId(storages()[0]?.id ?? '');
        setFormAnvilRadius(DEFAULT_ANVIL_RADIUS);
        setFormRepairBelowPercent(DEFAULT_REPAIR_BELOW_PERCENT);
        setFormMaxLevels(DEFAULT_MAX_LEVELS);
        setFormIsEnabled(true);
        setShowForm(true);
    }

    function openEdit(c: RepairConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormStorageId(c.storageId);
        setFormAnvilRadius(c.anvilRadius);
        setFormRepairBelowPercent(c.repairBelowPercent);
        setFormMaxLevels(c.maxLevelsPerRepair);
        setFormIsEnabled(c.isEnabled);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('REPAIR_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || t('REPAIR_DEFAULT_NAME'),
            storageId: formStorageId(),
            anvilRadius: formAnvilRadius(),
            repairBelowPercent: formRepairBelowPercent(),
            maxLevelsPerRepair: formMaxLevels(),
            isEnabled: formIsEnabled(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.repair.updateRepairConfig(id, body)
                : await api.repair.createRepairConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('REPAIR_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('REPAIR_UPDATED') : t('REPAIR_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('REPAIR_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.repair.deleteRepairConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('REPAIR_DELETE_FAILED'));
            return;
        }
        toast?.info(t('REPAIR_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('REPAIR_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.repair.runRepairConfig(id, { botId: targetBotId }),
            { success: t('REPAIR_STARTED'), failure: t('REPAIR_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('REPAIR_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.repair.stopRepair(id, { botId: targetBotId }),
            { success: t('REPAIR_STOPPED'), failure: t('REPAIR_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8);
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="REPAIR_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="REPAIR_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="REPAIR_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="REPAIR_EDIT"/> : <Text val="REPAIR_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="REPAIR_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('REPAIR_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="REPAIR_F_STORAGE"/></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('REPAIR_SELECT_STORAGE_OPTION')}
                                />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="REPAIR_F_RADIUS"/></span>
                                <input class={styles.input} type="number" min="0" max="32" value={formAnvilRadius()} onInput={e => setFormAnvilRadius(parseInt(e.currentTarget.value) || 0)} />
                                <span class={styles.fieldHint}><Text val="REPAIR_F_RADIUS_HINT"/></span>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="REPAIR_F_THRESHOLD"/></span>
                                <input class={styles.input} type="number" min="1" max="100" value={formRepairBelowPercent()} onInput={e => setFormRepairBelowPercent(parseInt(e.currentTarget.value) || DEFAULT_REPAIR_BELOW_PERCENT)} />
                                <span class={styles.fieldHint}><Text val="REPAIR_F_THRESHOLD_HINT"/></span>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="REPAIR_F_MAX_LEVELS"/></span>
                                <input class={styles.input} type="number" min="1" max="39" value={formMaxLevels()} onInput={e => setFormMaxLevels(parseInt(e.currentTarget.value) || DEFAULT_MAX_LEVELS)} />
                                <span class={styles.fieldHint}><Text val="REPAIR_F_MAX_LEVELS_HINT"/></span>
                            </label>
                        </div>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="REPAIR_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-hammer" />
                        <p><Text val="REPAIR_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>{storageName(c.storageId)}</div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="REPAIR_ENABLED"/> : <Text val="REPAIR_DISABLED"/>}
                                    </span>
                                </div>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}><i class="fa-solid fa-hammer" /> {t('REPAIR_INFO_RADIUS', String(c.anvilRadius))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-heart-crack" /> {t('REPAIR_INFO_THRESHOLD', String(c.repairBelowPercent))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-flask" /> {t('REPAIR_INFO_MAX_LEVELS', String(c.maxLevelsPerRepair))}</div>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="REPAIR_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
